import React, { useEffect } from 'react'
import { NextRouter, useRouter } from 'next/router'
import { useBogo } from '@mc/bogo-utils'
import ErrorBoundary from '@mc/error-boundary'
import { ErrorPage } from '@mc/error-page'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import { useIsPen7367Variant } from 'experiments/pen-7367_v1_quiz_subcat_removal/useIsPen7367Variant'
import {
  useCertsQuizPlansExperiment,
  useTrackPrcng1138Experiment,
} from 'experiments/prcng-1138_v1_certs_quiz_plans'
import { AI_MOTIVATION_QUERY_PARAM } from 'pages/find-my-classes/hooks/useCategorySelections'
import type { TCategory, TSubcategory } from '../../types'
import { QuizNavigation } from '../QuizNavigation'
import { Loader } from './components'
import { QuizMachineContext } from './context'
import { createStateHistoryInspector } from './createStateHistoryInspector'
import { quizMachine, STEP_NAME, TAGS } from './machine'
import {
  BogoOfferInterstitial,
  CertsInterstitial,
  FullAccess,
  LearningPreference,
  LearningTime,
  Motivation,
  OwnPace,
  SubcatInterests,
  MotivationRefinement,
  PersonalizedWayfinding,
} from './steps'

const STATES: Record<string, () => React.ReactNode> = {
  categoryInterests: () => <Motivation />,
  fullAccess: () => <FullAccess />,
  learningTime: () => <LearningTime />,
  // prcng-1138_v1
  learningPreferenceQuestion: () => <LearningPreference />,
  certsAddOnInterstitial: () => <CertsInterstitial />,
  ownPace: () => <OwnPace />,
  subcatInterests: () => <SubcatInterests />,
  bogoOfferInterstitial: () => <BogoOfferInterstitial />,
  motivationRefinementPage: () => <MotivationRefinement />,
  completeQuiz: () => <PersonalizedWayfinding />,
  // transient or action-only states with no UI
  initialize: () => null,
  homePage: () => null,
  subcatsInitialization: () => null,
  bogoOffer: () => null,
  learningPreference: () => null, // prcng-1138_v1
  certsAddOn: () => null, // prcng-1138_v1
  completed: () => null,
  // action-only states to handle quiz final animation
  'completeQuiz.unanimated': () => null,
  'completeQuiz.animating': () => null,
  'completeQuiz.animated': () => null,
  'completeQuiz.delayed': () => null,
} as const

type DefinedStates = keyof typeof STATES

const getCategoriesCSVFromUrl = (router: NextRouter) => {
  const c = router.query?.categories
  if (typeof c === 'string') return c
  return undefined
}

const QuizSteps = () => {
  const state = QuizMachineContext.useSelector((snapshot) => snapshot)

  // we should always scroll to the top of the page when the state changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [state])

  return (
    <>
      <div className='container mc-pb-5 mc-text--center'>
        {state.hasTag(TAGS.Loading) && <Loader />}
        {Object.entries(STATES).map(
          ([stateKey, StateComponent]) =>
            state.matches(stateKey as DefinedStates) && (
              <StateComponent key={stateKey} />
            ),
        )}
      </div>
    </>
  )
}

const getResultsUrl = () => {
  if (typeof window === 'undefined') return ''
  return `${paths.classRecommendationsResults}${window.location.search}`
}

export const Quiz = () => {
  const router = useRouter()
  const { categories, subcategories } = useStaticData<{
    categories: TCategory[]
    subcategories: TSubcategory[]
  }>()
  const { isBogoOfferEligible } = useBogo({ isGift: false })

  // prcng-1138_v1
  const { isVariant: isCertsQuizVariant } = useCertsQuizPlansExperiment()

  const isSubcatRemovalVariant = useIsPen7367Variant()

  const userHasSkipQ1Param =
    router.query?.[QUIZ_QUERY_PARAMS.SKIP_Q1] === QUIZ_QUERY_PARAMS.WAS_VIEWED

  const userHasSkipMotivationRefinementParam =
    router.query?.[QUIZ_QUERY_PARAMS.MOTIVATION_REFINEMENT_VIEWED] ===
    QUIZ_QUERY_PARAMS.WAS_VIEWED

  // AI-only users from homepage hero have no categories but should still skip Q1
  const hasAiMotivationParam =
    router.query?.[AI_MOTIVATION_QUERY_PARAM] === 'true'

  useTrackPrcng1138Experiment()

  if (!categories || !subcategories) {
    return <ErrorPage />
  }

  return (
    <ErrorBoundary>
      <div>
        <QuizMachineContext.Provider
          // note: type errors for `machine` are likely because of missing configuration in the machine definition.
          // e.g. missing states, actions, guards, etc.
          // please add these to the machine definition in machine.ts before adding them here
          // .withConfig and .withContext are the last opportunities to provide those missing properties
          // if they require some runtime logic unavailable for the machine definition
          logic={quizMachine.provide({
            actions: {
              prepareCompletion: () => {
                router.prefetch(getResultsUrl())
              },
              quizCompleted: () => {
                router.push(getResultsUrl())
              },
              goToHomePage: () => {
                router.push(paths.root)
              },
            },
          })}
          options={{
            input: {
              stepType: 'interactive',
              step: '1',
              stepHistory: ['1'],
              stepName: STEP_NAME.PRIMER_QUESTION,
              userHasSkipQ1Param,
              // prcng-1138_v1 adds one interactive step in variant
              totalSteps: isCertsQuizVariant ? 5 : 4,
              allCategories: categories,
              allSubcategories: subcategories,
              categoryInterestsCSV: getCategoriesCSVFromUrl(router),
              isBogoOfferEligible,
              hasAiMotivationParam,
              userHasSkipMotivationRefinementParam,
              // prcng-1138_v1:
              isCertsQuizVariant,
              isSubcatRemovalVariant,
            },
            inspect: createStateHistoryInspector(),
          }}
        >
          <QuizNavigation />
          <QuizSteps />
        </QuizMachineContext.Provider>
      </div>
    </ErrorBoundary>
  )
}
